import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Record a full or partial payment for a synced QuickBooks invoice.
 * Posts a QBO Payment (full amounts settle the invoice; partials leave the
 * remainder owing), then refreshes via onRecorded.
 */
export const RecordPaymentDialog = ({ invoice, onClose, onRecorded }) => {
  const [amount, setAmount] = useState(Number(invoice.balance).toFixed(2)); // default: full balance
  const [method, setMethod] = useState(""); // optional payment method, stored on the QBO payment
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    const amt = parseFloat(amount);
    if (!(amt > 0)) return;
    setBusy(true);
    try {
      const res = await api.post("/billing/mark-paid", { qbo_id: invoice.qbo_id, amount: amt, method: method || undefined });
      toast.success(res.data.settled
        ? `Invoice #${invoice.doc_number || invoice.qbo_id} paid in full${method ? ` (${method})` : ""} — QuickBooks updated`
        : `Recorded ${money(amt)}${method ? ` via ${method}` : ""} toward #${invoice.doc_number || invoice.qbo_id} — ${money(res.data.remaining)} still owing`);
      onRecorded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the payment in QuickBooks");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="mark-paid-dialog" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-xs rounded-lg bg-white p-4 shadow-xl">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-700">Record payment</p>
        <h3 className="text-sm font-bold text-gray-900">Invoice #{invoice.doc_number || invoice.qbo_id}</h3>
        <p className="mt-1 text-xs text-gray-500">Balance due: <span className="font-mono font-semibold text-gray-800">{money(invoice.balance)}</span></p>
        <div className="mt-3">
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gray-500">Amount received</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="mark-paid-amount"
              className="w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-sm text-gray-800 outline-none focus:border-emerald-500"
            />
            <button
              onClick={() => setAmount(Number(invoice.balance).toFixed(2))}
              data-testid="mark-paid-full"
              className="shrink-0 rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 hover:bg-emerald-100"
            >
              Full
            </button>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">A smaller amount records a partial payment in QuickBooks — the rest stays owing.</p>
          <label className="mb-1 mt-3 block font-mono text-[10px] uppercase tracking-widest text-gray-500">Payment method (optional)</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            data-testid="mark-paid-method"
            className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-800 outline-none focus:border-emerald-500"
          >
            <option value="">No method noted</option>
            <option value="e-transfer">E-Transfer</option>
            <option value="cheque">Cheque</option>
            <option value="cash">Cash</option>
          </select>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} data-testid="mark-paid-cancel" className="rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button
            onClick={confirm}
            disabled={busy || !(parseFloat(amount) > 0)}
            data-testid="mark-paid-confirm"
            className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Record in QuickBooks"}
          </button>
        </div>
      </div>
    </div>
  );
};
